import { TextField } from "@/app/fields";
import type { AlertMode, AlertRule } from "@/app/master";
import type { Errors } from "@/app/useDraft";
import { Combobox, Field, Form, Select, Textarea } from "@/ui";
import { ALERT_RULES, RULE_ORDER, type AlertThresholdDraft } from "./alertThresholdDraft";

type Option = { value: string; label: string; hint?: string };

interface Props {
  draft: AlertThresholdDraft; errors: Errors<AlertThresholdDraft>; set: <K extends keyof AlertThresholdDraft>(k: K, v: AlertThresholdDraft[K]) => void;
  isNew: boolean; channels: Option[]; accounts: Option[];
  /** 余额告警线用的币种：按账号所属客户的币种。 */
  currencyOf: (accountID: number | string) => string | undefined;
}

const RULE_OPTIONS = RULE_ORDER.map((r) => ({ value: r, label: ALERT_RULES[r].label }));

/** 数值框的标签与说明随规则与比较方式变（docs/07 第 2.4 节）。 */
function valueLabel(rule: AlertRule, mode: AlertMode, currency?: string): { label: string; hint: string } {
  if (rule === "channel_queue_wait") return { label: "排队时长上限（秒）", hint: "按当前发送速率估算发完积压的时间超过它就报，默认 120 秒" };
  if (rule === "balance_low") return { label: `告警线${currency ? `（${currency}）` : ""}`, hint: "余额低于这个金额就报；0 表示开始用信用额度时报" };
  if (rule === "channel_consecutive_failures") return { label: "连续失败次数", hint: "提交失败与回执失败都计，回执送达清零，按运营商分开数；默认 20" };
  if (rule === "account_consecutive_failures") return { label: "连续没送达条数", hint: "各通道合计，一条送达清零，按运营商分开数；默认 50" };
  return mode === "baseline"
    ? { label: "相对 7 天均值（%）", hint: "例如 60：低于这个维度自身 7 天平均送达率的 60% 才报，适合本来就偏低的通道" }
    : { label: "送达率下限（%）", hint: "近 1 小时送达率低于它就报，默认 50" };
}

/** 告警阈值表单的字段（决策 D190、D191）。作用对象建好后不改，编辑时不显示，改对象就新建一条。 */
export function AlertThresholdForm({ draft, errors, set, isNew, channels, accounts, currencyOf }: Props) {
  const meta = draft.rule ? ALERT_RULES[draft.rule] : null;
  const owners = meta?.owner === "account" ? accounts : channels;
  const modes = [{ value: "absolute", label: "固定值" }, ...(meta?.baseline ? [{ value: "baseline", label: "相对自身基线" }] : []), { value: "off", label: "不告警" }];
  const shown = draft.rule ? valueLabel(draft.rule, draft.mode, currencyOf(draft.owner_id)) : null;
  return (
    <Form>
      {isNew && (
        <>
          <Field label="规则" required error={errors.rule}>
            <Select block options={RULE_OPTIONS} value={draft.rule || null} placeholder="选择规则" invalid={!!errors.rule}
              onChange={(v) => { set("rule", v as AlertRule); set("owner_id", ""); set("mode", "absolute"); set("value", ""); }} />
          </Field>
          {meta && (
            <Field label={meta.owner === "account" ? "账号" : "通道"} hint={`留空表示全部${meta.owner === "account" ? "账号" : "通道"}`}>
              <Combobox block options={[{ value: "", label: meta.owner === "account" ? "全部账号" : "全部通道" }, ...owners]} value={draft.owner_id}
                onChange={(v) => set("owner_id", v)} />
            </Field>
          )}
          {meta?.operator && (
            <>
              <TextField id="at-mcc" label="MCC" mono value={draft.mcc} error={errors.mcc} hint="留空表示不按国家区分" onChange={(v) => set("mcc", v.trim())} />
              <TextField id="at-mnc" label="MNC" mono value={draft.mnc} error={errors.mnc} hint="留空表示该 MCC 的全部运营商" onChange={(v) => set("mnc", v.trim())} />
            </>
          )}
        </>
      )}
      {meta && (
        <>
          <Field label="比较方式" required error={errors.mode}>
            <Select block options={modes} value={draft.mode} onChange={(v) => set("mode", v as AlertMode)} invalid={!!errors.mode} />
          </Field>
          {draft.mode !== "off" && shown && (
            <TextField id="at-value" label={shown.label} required mono value={draft.value} error={errors.value} hint={shown.hint} onChange={(v) => set("value", v.trim())} />
          )}
        </>
      )}
      <Field label="备注" htmlFor="at-note"><Textarea id="at-note" rows={3} value={draft.note} onChange={(e) => set("note", e.target.value)} /></Field>
    </Form>
  );
}
